import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';

import { Subscription } from 'rxjs';

import { EnvironmentService } from 'src/app/services/environment.service';
import { AuthService } from '../../services/auth.service';
import { Auth0UserModel } from '../../models/auth0-user.model';
import { BaseComponent } from 'src/app/components/base/base.component';
import { MonitoringService } from 'src/app/services/monitoring.service';

@Component({
	templateUrl: './auth-menu.component.html',
	styleUrls: [
		'./../../../../../styles.scss',
		'./auth-menu.component.scss'
	],
	encapsulation: ViewEncapsulation.ShadowDom
})
export class AuthMenuComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
	/** 
	* language URL parameter value, e.g. 'es' 
	* NOT THE SAME AS LANGUAGE KEY
	*/
	@Input() language!: string;
	/** 
	 * Auth0 will return to this URL after login/signup process, 
	 * this URL should be in Auth0 allowed callbacks.
	 * If not defined, the current URL (where this component is placed) will be used
	*/
	@Input() loginCallbackUrl!: string;
	/** 
	 * Auth0 will return to this URL after logout process,
	 * this URL should be in Auth0 allowed logout callbacks.
	 * If not defined, the current URL (where this component is placed) will be used
	*/
	@Input() logoutCallbackUrl!: string;
	@Input() isAgentMenu: boolean = false;
	@Output() isAuthenticated = new EventEmitter<boolean>();
	@Output() authenticatedUser = new EventEmitter<Auth0UserModel>();

	private isLoggedInSubscription!: Subscription;
	private userSubscription!: Subscription;
	private errorSubscription!: Subscription;

	/** mobile, full width account menu */
	isAuthMenuCollapsed: boolean = true;
	/** mobile, full width join menu */
	isJoinMenuCollapsed: boolean = true;
	isLoggedIn: boolean = false;
	userEmail?: string;
	profileLink!: string;
	bookingsLink!: string;
	linkTravelAgents?: string;
	linkTransportCompanies?: string;
	linkAffiliates?: string;

	constructor(
		private authService: AuthService,
		private monitoringService: MonitoringService,
		protected override environmentService: EnvironmentService,
	) {
		super(environmentService);
	}

	ngOnInit() {
		this.defineLinks();
		this.isLoggedInSubscription = this.authService.isLoggedIn$.subscribe(res => {
			this.isLoggedIn = res;
			this.isAuthenticated.emit(this.isLoggedIn);
		});
		this.userSubscription = this.authService.user$.subscribe(res => {
			this.authenticatedUser.emit(res);
			this.userEmail = res?.email;
		});
		this.errorSubscription = this.monitoringService.error$.subscribe(err => {
			this.onError.emit(err);
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['language']) {
			this.defineLinks();
		}
		if (changes['customEnvironment']) {
			this.defineEnvironment();
		}
	}

	ngOnDestroy(): void {
		this.isLoggedInSubscription?.unsubscribe();
		this.userSubscription?.unsubscribe();
		this.errorSubscription?.unsubscribe();
	}

	private defineLinks() {
		const bookingAppUrl = this.environmentService.getBookingAppUrl();
		const langParam = this.language ? `${this.language}/` : '';
		this.profileLink = `${bookingAppUrl}/${langParam}membership/profile`;
		this.bookingsLink = `${bookingAppUrl}/${langParam}membership/bookings`;

		const jpressUrl = this.environmentService.getJpressUrl();
		this.linkTravelAgents = `${jpressUrl}/${langParam}travel-agents/`;
		this.linkTransportCompanies = `${jpressUrl}/${langParam}transport-companies/`;
		this.linkAffiliates = `${jpressUrl}/${langParam}jayride-affiliates/`;
	}

	toggleAuthMenu() {
		this.isAuthMenuCollapsed = !this.isAuthMenuCollapsed;
	}

	collapseAuthMenu() {
		this.isAuthMenuCollapsed = true;
	}

	toggleJoinMenu() {
		this.isJoinMenuCollapsed = !this.isJoinMenuCollapsed;
	}

	collapseJoinMenu() {
		this.isJoinMenuCollapsed = true;
	}

	logIn(isSignUp: boolean) {
		this.authService.logIn(isSignUp, this.loginCallbackUrl, this.language);
	}

	logOut() {
		this.authService.logOut(this.logoutCallbackUrl);
	}

	signUpAsAgent() {
		this.authService.logIn(true, this.loginCallbackUrl, this.language, true);
	}
}
